// Student service. API calls for student dashboard, gamification, notifications, and game completion.
import { apiGet, apiPost, apiPatch } from './apiClient';

export interface StudentDashboardData {
  wellness: {
    score: number;
    screenTime: string;
    screenTimeMinutes: number;
    todayEvents: number;
    recentFlaggedEvents: number;
    message: string;
  };
  activity: {
    todayEvents: number;
    screenTime: string;
    points: number;
    level: number;
    levelName: string;
  };
  rank: {
    position: number;
    totalClassmates: number;
    percentile: number;
  };
  weeklyProgress: {
    days: Array<{ day: string; points: number; date: string }>;
    totalPoints: number;
    trend: string;
  };
  alerts: {
    recentCount: number;
  };
}

export interface GameData {
  id: string;
  name: string;
  description: string | null;
  maxPoints: number;
  status: 'completed' | 'in_progress' | 'not_started';
  pointsEarned: number;
  lastPlayed: string | null;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  avatarUrl: string | null;
  points: number;
  level: number;
  isCurrentUser: boolean;
}

export interface GamificationData {
  points: number;
  level: number;
  levelName: string;
  nextLevelPoints: number;
  progressToNextLevel: number;
  games: GameData[];
  achievements: Achievement[];
  leaderboard: LeaderboardEntry[];
  weeklyChallenge: {
    title: string;
    description: string;
    completed: number;
    target: number;
    bonusPoints: number;
    isComplete: boolean;
  };
}

export interface StudentNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'urgent' | 'critical';
  status: 'read' | 'unread';
  senderName: string;
  createdAt: string;
}

export const studentService = {
  async getDashboard(): Promise<StudentDashboardData> {
    const response = await apiGet<{ success: boolean; dashboard: StudentDashboardData }>('/student/dashboard');
    return response.dashboard;
  },

  async getGamification(): Promise<GamificationData> {
    const response = await apiGet<{ success: boolean; gamification: GamificationData }>('/student/gamification');
    return response.gamification;
  },

  async completeGame(gameId: string, pointsEarned?: number): Promise<{
    success: boolean;
    pointsEarned: number;
    pointsAwarded: number;
    bestScore: number;
    totalPoints: number;
    level: number;
    levelName: string;
    message: string;
  }> {
    return apiPost('/student/games/complete', { gameId, pointsEarned });
  },

  async getNotifications(): Promise<{ notifications: StudentNotification[]; unreadCount: number }> {
    const response = await apiGet<{ success: boolean; notifications: StudentNotification[]; unreadCount: number }>('/student/notifications');
    return { notifications: response.notifications, unreadCount: response.unreadCount };
  },

  async markNotificationRead(id: string): Promise<void> {
    await apiPatch(`/student/notifications/${id}/read`);
  }
};
